import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import { apiFetch } from "../../api";

function normalizePhone(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 10);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function statusLabel(estado) {
  if (estado === "ACTIVO") return "Activa";
  if (estado === "FUTURO") return "Futuro";
  if (estado === "VENTA") return "Venta";
  if (estado === "BAJA") return "Baja";
  return estado || "-";
}

function statusColor(estado) {
  if (estado === "ACTIVO") return "success";
  if (estado === "FUTURO") return "default";
  if (estado === "VENTA") return "success";
  if (estado === "BAJA") return "error";
  return "default";
}

function useVehicleOptions() {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const controllerRef = React.useRef(null);

  const fetchOptions = React.useCallback(async (q) => {
    if (controllerRef.current) controllerRef.current.abort();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;
    try {
      setLoading(true);
      const res = await apiFetch(`/api/vehiculos/?q=${encodeURIComponent(q || "")}`, {
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error("no response");
      const data = await res.json();
      setOptions(
        (Array.isArray(data) ? data : [])
          .map((x) => {
            if (typeof x === "string") return { id: null, label: x };
            return { id: x.id ?? null, label: x.modelo || x.nombre || x.name };
          })
          .filter((o) => o && o.label)
      );
    } catch (e) {
      if (e.name !== "AbortError") setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { options, fetchOptions, loading };
}

const emptyClient = (telefono = "") => ({
  nombre: "",
  telefono,
  email: "",
  obtenido_de: "",
  observacion: "",
});

export default function CrearLeadVendedorDialog({ open, onClose, onCreated, currentVendedorId }) {
  const [step, setStep] = React.useState("telefono");
  const [telefono, setTelefono] = React.useState("");
  const [matches, setMatches] = React.useState([]);
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  const [clientForm, setClientForm] = React.useState(emptyClient());
  const [consultaInput, setConsultaInput] = React.useState("");
  const [selectedFuture, setSelectedFuture] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const vehicleApi = useVehicleOptions();

  const selectedClient = React.useMemo(
    () => matches.find((m) => String(m.id) === String(selectedClientId)) || null,
    [matches, selectedClientId]
  );
  const foundExistingClient = (step === "elegir" || step === "cliente") && matches.length > 0;

  React.useEffect(() => {
    if (!open) {
      setStep("telefono");
      setTelefono("");
      setMatches([]);
      setSelectedClientId(null);
      setClientForm(emptyClient());
      setConsultaInput("");
      setSelectedFuture(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  const closeAndNotify = (consulta = null, cliente = selectedClient) => {
    onCreated?.({ consulta, cliente });
    onClose?.();
  };

  const dispatchOpenSeguimiento = (consulta, cliente = selectedClient) => {
    if (!consulta) return;
    window.dispatchEvent(new CustomEvent("open_seguimiento_asignacion", {
      detail: {
        id: consulta.id,
        id_ven_cli: consulta.id,
        id_cliente: consulta.id_cliente || cliente?.id,
        nombreCliente: cliente?.nombre || clientForm.nombre,
        consulta: consulta.vehiculo_consultado,
        estado: consulta.estado || "ACTIVO",
        id_vendedor: consulta.id_vendedor,
        nombreVendedor: consulta.vendedor_nombre,
        estadioActual: "INICIO",
      },
    }));
  };

  const followExistingAndClose = (consulta, cliente = selectedClient) => {
    onCreated?.({ consulta, cliente });
    dispatchOpenSeguimiento(consulta, cliente);
    onClose?.();
  };

  const searchPhone = async () => {
    const digits = normalizePhone(telefono);
    if (!digits) {
      setError("Ingresá un teléfono.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/vendedor/leads/buscar-telefono?telefono=${encodeURIComponent(digits)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const found = Array.isArray(data.matches) ? data.matches : [];
      setMatches(found);
      if (found.length === 0) {
        setClientForm(emptyClient(digits));
        setStep("nuevo");
      } else if (found.length === 1) {
        setSelectedClientId(found[0].id);
        setClientForm({ ...emptyClient(digits), ...found[0], telefono: found[0].telefono || digits });
        setStep("cliente");
      } else {
        setStep("elegir");
      }
    } catch (e) {
      setError("No se pudo buscar el teléfono.");
    } finally {
      setLoading(false);
    }
  };

  const selectClient = (client) => {
    setSelectedClientId(client.id);
    setClientForm({ ...emptyClient(telefono), ...client });
  };

  const saveClientAndContinue = async () => {
    if (!selectedClient) return;
    if (!clientForm.nombre?.trim()) {
      setError("El nombre del cliente es obligatorio.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        nombre: clientForm.nombre.trim(),
        telefono: normalizePhone(telefono || clientForm.telefono),
        email: clientForm.email?.trim() || null,
        obtenido_de: clientForm.obtenido_de?.trim() || null,
        observacion: clientForm.observacion?.trim() || null,
      };
      const res = await apiFetch(`/api/clientes/${selectedClient.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setMatches((prev) => prev.map((m) => String(m.id) === String(updated.id) ? updated : m));
      setClientForm(updated);
      setStep("consultas");
    } catch (e) {
      setError(e.message || "No se pudo guardar el cliente.");
    } finally {
      setLoading(false);
    }
  };

  const createNewClient = async () => {
    const vehiculo = consultaInput.trim();
    if (!clientForm.nombre?.trim()) {
      setError("El nombre del cliente es obligatorio.");
      return;
    }
    if (!vehiculo) {
      setError("El vehículo consultado es obligatorio.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        nombre: clientForm.nombre.trim(),
        telefono: normalizePhone(clientForm.telefono || telefono),
        email: clientForm.email?.trim() || null,
        obtenido_de: clientForm.obtenido_de?.trim() || null,
        observacion: clientForm.observacion?.trim() || null,
        consulta: vehiculo,
        consultas: [{ vehiculo_consultado: vehiculo }],
      };
      const res = await apiFetch("/api/clientes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "No se pudo crear el lead.");
      }
      const created = await res.json();
      const consulta = (created.consultas || [])[0] || { id: created.id_ven_cli, id_cliente: created.id, vehiculo_consultado: vehiculo, estado: "ACTIVO" };
      closeAndNotify(consulta, created);
    } catch (e) {
      setError(e.message || "No se pudo crear el lead.");
    } finally {
      setLoading(false);
    }
  };

  const createConsulta = async (force = false) => {
    const vehiculo = consultaInput.trim();
    if (!vehiculo) {
      setError("El vehículo consultado es obligatorio.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/vendedor/leads/clientes/${selectedClient.id}/consultas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ vehiculo_consultado: vehiculo, force_duplicate_active: force }),
      });
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        const existing = data.existing_consulta;
        const createAnyway = window.confirm(
          "Hay una consulta activa con el mismo vehículo. Aceptar para crear otra consulta igualmente, o Cancelar para seguir con la consulta existente."
        );
        if (createAnyway) {
          setLoading(false);
          return createConsulta(true);
        } else {
          setLoading(false);
          followExistingAndClose(existing, selectedClient);
        }
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "No se pudo crear la consulta.");
      }
      const data = await res.json();
      closeAndNotify(data.consulta, selectedClient);
    } catch (e) {
      setError(e.message || "No se pudo crear la consulta.");
    } finally {
      setLoading(false);
    }
  };

  const reactivateFuture = async () => {
    if (!selectedFuture) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/vendedor/leads/reactivar-futuro", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ consulta_id: selectedFuture.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "No se pudo reactivar la consulta.");
      }
      const consulta = await res.json();
      closeAndNotify(consulta, selectedClient);
    } catch (e) {
      setError(e.message || "No se pudo reactivar la consulta.");
    } finally {
      setLoading(false);
    }
  };

  const canConfirmConsulta = selectedFuture || consultaInput.trim();

  const renderClientFields = () => (
    <Stack spacing={1.5}>
      <TextField label="Nombre" value={clientForm.nombre || ""} onChange={(e) => setClientForm((p) => ({ ...p, nombre: e.target.value }))} required />
      <TextField label="Teléfono" value={clientForm.telefono || ""} disabled inputProps={{ inputMode: "numeric" }} />
      <TextField label="Email" type="email" value={clientForm.email || ""} onChange={(e) => setClientForm((p) => ({ ...p, email: e.target.value }))} />
      <TextField label="Origen" value={clientForm.obtenido_de || ""} onChange={(e) => setClientForm((p) => ({ ...p, obtenido_de: e.target.value }))} />
      <TextField label="Observación" value={clientForm.observacion || ""} onChange={(e) => setClientForm((p) => ({ ...p, observacion: e.target.value }))} multiline minRows={3} />
    </Stack>
  );

  const renderConsultaList = () => {
    const consultas = selectedClient?.consultas || [];
    if (consultas.length === 0) {
      return <Box sx={{ border: "1px dashed #c9d7ea", borderRadius: "8px", p: 2, color: "#4c5d86" }}>Este cliente no tiene consultas previas.</Box>;
    }
    return (
      <Stack spacing={1}>
        {consultas.map((c) => {
          const sameSellerFuture = c.estado === "FUTURO" && currentVendedorId != null && String(c.id_vendedor) === String(currentVendedorId);
          return (
            <Box key={c.id} sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 1.25 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, color: "#111827" }}>{c.vehiculo_consultado || "Sin vehículo"}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#4c5d86" }}>
                    {c.vendedor_nombre || "Sin asignar"} · {c.estado === "FUTURO" ? (c.fecha_programada ? `Para ${formatDate(c.fecha_programada)}` : "Sin fecha programada") : c.estado === "ACTIVO" ? `Desde ${formatDate(c.fecha_inicio)}` : `Cerró ${formatDate(c.fecha_cierre)}`}
                  </Typography>
                </Box>
                <Chip size="small" label={statusLabel(c.estado)} color={statusColor(c.estado)} />
              </Box>
              {sameSellerFuture && (
                <Button
                  size="small"
                  variant={selectedFuture?.id === c.id ? "contained" : "outlined"}
                  onClick={() => {
                    setSelectedFuture((prev) => (prev?.id === c.id ? null : c));
                    setConsultaInput("");
                  }}
                  sx={{ mt: 1 }}
                >
                  Reactivar consulta
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    );
  };

  // The backend still enforces ownership; the UI enables reactivation for any FUTURO row that came back with the current seller.
  const enableFutureButtons = React.useMemo(() => selectedClient?.consultas || [], [selectedClient]);
  void enableFutureButtons;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 24, fontWeight: 900 }}>Crear lead</Typography>
            {foundExistingClient && (
              <Typography sx={{ fontSize: 12, color: "#7b8798", fontWeight: 700 }}>
                Se encontró cliente existente
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} disabled={loading}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      {loading && <LinearProgress />}
      <DialogContent sx={{ px: 3, pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === "telefono" && (
          <Stack spacing={2}>
            <Typography sx={{ color: "#4c5d86" }}>Ingresá el teléfono del cliente que consulta.</Typography>
            <TextField
              autoFocus
              label="Teléfono"
              value={telefono}
              onChange={(e) => setTelefono(normalizePhone(e.target.value))}
              inputProps={{ inputMode: "numeric" }}
              InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: "#4c5d86" }} /> }}
              onKeyDown={(e) => { if (e.key === "Enter") searchPhone(); }}
            />
          </Stack>
        )}

        {step === "elegir" && (
          <Stack spacing={2}>
            <Typography sx={{ fontWeight: 800 }}>Encontré más de un cliente con ese teléfono. Elegí cuál corresponde.</Typography>
            <List sx={{ border: "1px solid #e4ebf5", borderRadius: "8px" }}>
              {matches.map((client) => (
                <ListItemButton key={client.id} selected={String(selectedClientId) === String(client.id)} onClick={() => selectClient(client)}>
                  <PersonIcon sx={{ mr: 1, color: "#0059f6" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>{client.nombre}</Typography>
                    <Typography sx={{ fontSize: 12, color: "#4c5d86" }}>{client.telefono} · {(client.consultas || []).length} consultas</Typography>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Stack>
        )}

        {step === "cliente" && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Información del cliente</Typography>
              {renderClientFields()}
            </Box>
            <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", mt: 4, p: 2, alignSelf: "start" }}>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Resumen</Typography>
              <Typography>{(selectedClient?.consultas || []).length} consultas registradas</Typography>
              <Typography sx={{ color: "#4c5d86", fontSize: 13, mt: 1 }}>Podés actualizar los datos básicos antes de continuar.</Typography>
            </Box>
          </Box>
        )}

        {step === "consultas" && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Consultas del cliente</Typography>
              {renderConsultaList()}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Nueva consulta</Typography>
              <Autocomplete
                freeSolo
                options={vehicleApi.options}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || "")}
                inputValue={consultaInput}
                disabled={Boolean(selectedFuture)}
                onInputChange={(e, value, reason) => {
                  setConsultaInput(value);
                  if (reason === "input") vehicleApi.fetchOptions(value);
                }}
                onOpen={() => vehicleApi.fetchOptions("")}
                onChange={(e, value) => setConsultaInput(typeof value === "string" ? value : value?.label || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehículo consultado"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {vehicleApi.loading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {selectedFuture && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Se reactivará la consulta futura seleccionada y se eliminará su programación pendiente.
                </Alert>
              )}
            </Box>
          </Box>
        )}

        {step === "nuevo" && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Nuevo cliente</Typography>
              {renderClientFields()}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Consulta obligatoria</Typography>
              <Autocomplete
                freeSolo
                options={vehicleApi.options}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || "")}
                inputValue={consultaInput}
                onInputChange={(e, value, reason) => {
                  setConsultaInput(value);
                  if (reason === "input") vehicleApi.fetchOptions(value);
                }}
                onOpen={() => vehicleApi.fetchOptions("")}
                onChange={(e, value) => setConsultaInput(typeof value === "string" ? value : value?.label || "")}
                renderInput={(params) => <TextField {...params} label="Vehículo consultado" />}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {step !== "telefono" && (
          <Button disabled={loading} onClick={() => {
            if (step === "elegir" || step === "nuevo") setStep("telefono");
            else if (step === "cliente") setStep(matches.length > 1 ? "elegir" : "telefono");
            else if (step === "consultas") setStep("cliente");
          }}>
            Anterior
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        {step === "telefono" && <Button variant="contained" onClick={searchPhone} disabled={loading}>Continuar</Button>}
        {step === "elegir" && <Button variant="contained" onClick={() => selectedClientId && setStep("cliente")} disabled={!selectedClientId || loading}>Continuar</Button>}
        {step === "cliente" && <Button variant="contained" onClick={saveClientAndContinue} disabled={loading}>Continuar</Button>}
        {step === "consultas" && <Button variant="contained" onClick={() => selectedFuture ? reactivateFuture() : createConsulta(false)} disabled={loading || !canConfirmConsulta}>Confirmar</Button>}
        {step === "nuevo" && <Button variant="contained" onClick={createNewClient} disabled={loading || !consultaInput.trim()}>Confirmar</Button>}
      </DialogActions>
    </Dialog>
  );
}
